import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss, Card, CardTitle } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  INOUT_OPTIONS,
  LEVEL_CODE_OPTIONS,
  RULE_OPTIONS,
  YN_OPTIONS,
  DOWNSTREAM_IMPACT_SCREENS,
  clonePreset,
  validateStructure,
  loadStructurePayload,
  saveStructurePayload,
  buildBudgetLevelColumnFields,
  canUnconfirmStructure,
  normalizeStructureGridRows,
} from 'budget/budgetStructureStore';
import { migrateSubjectYear } from 'budget/budgetYearMigrate';
import YearMigrateDialog from 'components/budget/YearMigrateDialog';

/** 검색조건에서 제외한 목적구분 — 저장 키 호환용 고정값 */
const STRUCTURE_PURPOSE = '집행';

const GRID_ID = 'budget-subject-structure';

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1.35fr 0.65fr;
  gap: 12px;
  flex: 1;
  min-height: 0;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled(Card)`
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
  overflow: hidden;
`;

const PanelHead = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid ${theme.colors.border};
  font-weight: 700;
  font-size: ${theme.typography.fontSize.sm};
`;

const PreviewPath = styled.div`
  margin: 12px 14px;
  padding: 10px 12px;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  font-weight: 700;
  font-size: ${theme.typography.fontSize.sm};
`;

const CheckList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 14px 14px;
`;

const CheckItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${theme.colors.borderLight || theme.colors.border};
  font-size: ${theme.typography.fontSize.sm};
`;

const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${theme.typography.fontSize.xs};
  font-weight: 700;
  background: ${(p) => (p.$ok ? '#D1FAE5' : '#FEE2E2')};
  color: ${(p) => (p.$ok ? '#047857' : '#B91C1C')};
`;

const Hint = styled.p`
  margin: 0 14px 14px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
  line-height: 1.5;
`;

const MetaLine = styled.div`
  margin: 0 14px 10px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.5;
`;

const ImpactList = styled.ul`
  margin: 0 14px 14px;
  padding-left: 18px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
`;

/**
 * 예산과목구조관리 — 레벨체계 정의·확정
 * 예산구분·적용시작/종료는 검색·확정 조건에서 사용하지 않음.
 */
const BudgetSubjectStructureInner = () => {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const year = String(new Date().getFullYear());

  const searchDefaults = {
    tenantCode,
    fiscalYear: year,
    inoutDiv: '지출',
    status: '작성중',
  };

  const methods = useForm({ mode: 'onSubmit', defaultValues: { ...searchDefaults } });
  const { getValues, setValue, watch } = methods;
  const inoutDiv = watch('inoutDiv');
  const [statusLabel, setStatusLabel] = useState('작성중');
  const [meta, setMeta] = useState({ confirmedBy: '', confirmedAt: '', versionNo: 1 });

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  const isLocked = statusLabel === '확정';

  const columns = useMemo(
    () => [
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 70, options: YN_OPTIONS }),
      hiCol.edComboColumn({ header: '레벨', field: 'levelCode', size: 90, options: LEVEL_CODE_OPTIONS }),
      hiCol.edTextColumn({ header: '레벨명', field: 'levelName', size: 100, required: true }),
      hiCol.edComboColumn({ header: '최종레벨', field: 'finalYn', size: 80, options: YN_OPTIONS }),
      hiCol.edComboColumn({ header: '분개매핑', field: 'acctMapYn', size: 80, options: YN_OPTIONS }),
      hiCol.edComboColumn({ header: '코드규칙', field: 'codeRule', size: 120, options: RULE_OPTIONS }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: {
      levelNo: 1,
      useYn: 'Y',
      levelCode: 'LV1',
      levelName: '',
      finalYn: 'N',
      acctMapYn: 'N',
      codeRule: '자체',
    },
  });

  const [validationTick, setValidationTick] = useState(0);
  const liveRows = useMemo(() => gridRef.current?.getData?.() || [], [validationTick, grid.loading]); // eslint-disable-line react-hooks/exhaustive-deps
  const validation = useMemo(() => validateStructure(liveRows), [liveRows]);

  const refreshValidation = useCallback(() => {
    setValidationTick((n) => n + 1);
  }, []);

  useEffect(() => {
    grid.onAfterDataLoaded = () => refreshValidation();
    grid.onAfterSaveCell = () => refreshValidation();
  }, [grid, refreshValidation]);

  useEffect(() => {
    ['useYn', 'levelCode', 'levelName', 'finalYn', 'acctMapYn', 'codeRule'].forEach((f) => {
      grid.setColOption?.(f, { readOnly: isLocked });
    });
  }, [grid, isLocked]);

  const dimsFromForm = useCallback(() => {
    const v = getValues();
    return {
      tenantCode: v.tenantCode,
      fiscalYear: v.fiscalYear,
      inoutDiv: v.inoutDiv,
      purpose: STRUCTURE_PURPOSE,
    };
  }, [getValues]);

  const applyPayloadOrPreset = useCallback(async () => {
    const dims = dimsFromForm();
    const data = await loadStructurePayload(dims);
    if (data?.rows?.length) {
      grid.setData(normalizeStructureGridRows(data.rows));
      const st = data.status || '작성중';
      setStatusLabel(st);
      setValue('status', st);
      setMeta({
        confirmedBy: data.confirmedBy || '',
        confirmedAt: data.confirmedAt || '',
        versionNo: data.versionNo || 1,
      });
    } else {
      grid.setData(clonePreset(dims.inoutDiv, dims.purpose));
      setStatusLabel('작성중');
      setValue('status', '작성중');
      setMeta({ confirmedBy: '', confirmedAt: '', versionNo: 1 });
    }
    refreshValidation();
  }, [dimsFromForm, grid, setValue, refreshValidation]);

  useEffect(() => {
    applyPayloadOrPreset().catch((e) => {
      showAlert(e?.message || '구조 조회에 실패했습니다.', 'error');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshValidation();
  }, [inoutDiv, refreshValidation]);

  const handleSearch = useCallback(async () => {
    try {
      await applyPayloadOrPreset();
      showAlert('구조를 불러왔습니다.', 'success');
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
    }
  }, [applyPayloadOrPreset]);

  const buildPayload = useCallback(
    (status, extra = {}) => {
      const rows = normalizeStructureGridRows(gridRef.current?.getData?.() || []);
      const v = validateStructure(rows);
      const dims = dimsFromForm();
      const y = String(dims.fiscalYear || year);
      return {
        dims,
        purpose: dims.purpose,
        status,
        versionNo: Number(extra.versionNo ?? meta.versionNo) || 1,
        startDate: `${y}-01-01`,
        endDate: `${y}-12-31`,
        rows,
        usedLevels: v.used,
        finalLevelName: v.finalLevelName,
        acctMapLevelNames: v.acctMapLevelNames,
        levelColumns: buildBudgetLevelColumnFields(v.used),
        confirmedBy: extra.confirmedBy ?? meta.confirmedBy ?? null,
        confirmedAt: extra.confirmedAt ?? meta.confirmedAt ?? null,
        unconfirmHistory: extra.unconfirmHistory,
        savedAt: new Date().toISOString(),
        savedBy: user?.id || '',
      };
    },
    [dimsFromForm, user, meta, year]
  );

  const handleSave = useCallback(async () => {
    if (isLocked) {
      showAlert('확정된 구조는 저장할 수 없습니다. 확정해제 후 수정하세요.', 'warning');
      return;
    }
    const rows = normalizeStructureGridRows(gridRef.current?.getData?.() || []);
    const v = validateStructure(rows);
    if (!v.hasNames) {
      showAlert('사용중인 레벨명은 필수입니다.', 'warning');
      return;
    }
    const payload = buildPayload('작성중');
    try {
      await saveStructurePayload(dimsFromForm(), payload);
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
      return;
    }
    grid.setData(payload.rows);
    setValue('status', '작성중');
    showAlert('저장되었습니다.', 'success');
    refreshValidation();
  }, [buildPayload, dimsFromForm, grid, isLocked, setValue, refreshValidation]);

  const handleConfirm = useCallback(async () => {
    const rows = normalizeStructureGridRows(gridRef.current?.getData?.() || []);
    const v = validateStructure(rows);
    if (!v.canConfirm) {
      showAlert('레벨(LV) 연속 · 최종레벨 1건 · 레벨명을 확인하세요.', 'warning');
      return;
    }
    const dims = dimsFromForm();
    const mapText = v.acctMapLevelNames?.length ? v.acctMapLevelNames.join(', ') : '(없음)';
    const ok = await showConfirm(
      `구조를 확정할까요?\n수지구분: ${dims.inoutDiv}\n회계연도: ${dims.fiscalYear}\n체계: ${v.previewText}\n분개가능 레벨: ${mapText}\n\n확정 후 레벨 변경이 잠기며 하위 화면에 반영됩니다.`,
      'warning'
    );
    if (!ok) return;
    const confirmedAt = new Date().toISOString();
    const confirmedBy = user?.id || user?.name || '';
    let payload;
    try {
      const prev = await loadStructurePayload(dims);
      payload = buildPayload('확정', {
        confirmedAt,
        confirmedBy,
        versionNo: prev?.versionNo || meta.versionNo || 1,
        unconfirmHistory: prev?.unconfirmHistory || [],
      });
      await saveStructurePayload(dims, payload);
    } catch (e) {
      showAlert(e?.message || '확정에 실패했습니다.', 'error');
      return;
    }
    grid.setData(payload.rows);
    setStatusLabel('확정');
    setValue('status', '확정');
    setMeta({ confirmedBy, confirmedAt, versionNo: payload.versionNo });
    showAlert(`구조가 확정되었습니다.\n${v.previewText}`, 'success');
    refreshValidation();
  }, [buildPayload, dimsFromForm, grid, setValue, refreshValidation, user, meta.versionNo]);

  const handleUnconfirm = useCallback(async () => {
    if (!isLocked) {
      showAlert('확정 상태가 아닙니다.', 'info');
      return;
    }
    const dims = dimsFromForm();
    let gate;
    try {
      gate = await canUnconfirmStructure(dims);
    } catch (e) {
      showAlert(e?.message || '확정해제 가능 여부를 확인할 수 없습니다.', 'error');
      return;
    }
    if (!gate.ok) {
      showAlert(gate.reason, 'warning');
      return;
    }
    const ok = await showConfirm('확정을 해제하고 작성중으로 되돌릴까요?', 'warning');
    if (!ok) return;
    let payload;
    try {
      const prev = (await loadStructurePayload(dims)) || {};
      const history = [
        ...(prev.unconfirmHistory || []),
        {
          at: new Date().toISOString(),
          by: user?.id || '',
          prevConfirmedAt: prev.confirmedAt,
          prevConfirmedBy: prev.confirmedBy,
        },
      ];
      payload = buildPayload('작성중', {
        confirmedAt: null,
        confirmedBy: null,
        unconfirmHistory: history,
        versionNo: prev.versionNo || 1,
      });
      await saveStructurePayload(dims, payload);
    } catch (e) {
      showAlert(e?.message || '확정해제에 실패했습니다.', 'error');
      return;
    }
    grid.setData(payload.rows);
    setStatusLabel('작성중');
    setValue('status', '작성중');
    setMeta({ confirmedBy: '', confirmedAt: '', versionNo: payload.versionNo });
    showAlert('확정이 해제되었습니다.', 'success');
    refreshValidation();
  }, [isLocked, dimsFromForm, buildPayload, grid, user, setValue, refreshValidation]);

  const [migrateOpen, setMigrateOpen] = useState(false);

  const handleOpenMigrate = useCallback(async () => {
    const dims = dimsFromForm();
    let data;
    try {
      data = await loadStructurePayload(dims);
    } catch (e) {
      showAlert(e?.message || '과목구조 조회에 실패했습니다.', 'error');
      return;
    }
    if (!data?.rows?.length) {
      showAlert('이관할 과목구조가 없습니다. 먼저 조회·저장하세요.', 'warning');
      return;
    }
    setMigrateOpen(true);
  }, [dimsFromForm]);

  const handleMigrateConfirm = useCallback(async () => {
    const dims = dimsFromForm();
    let result;
    try {
      const src = await loadStructurePayload(dims);
      if (src?.status !== '확정') {
        const ok = await showConfirm(
          '현재 연도 구조가 확정되지 않았습니다. 그래도 차년도이관 할까요?',
          'warning'
        );
        if (!ok) return;
      }
      result = await migrateSubjectYear(dims, { userId: user?.id || '' });
    } catch (e) {
      setMigrateOpen(false);
      showAlert(e?.message || '차년도이관에 실패했습니다.', 'error');
      return;
    }
    setMigrateOpen(false);
    if (!result.ok) {
      showAlert(result.reason, 'warning');
      return;
    }
    setValue('fiscalYear', result.nextDims.fiscalYear);
    try {
      await applyPayloadOrPreset();
    } catch (e) {
      showAlert(e?.message || '이관 후 조회에 실패했습니다.', 'error');
      return;
    }
    showAlert(result.message || '차년도이관이 완료되었습니다.', 'success');
  }, [dimsFromForm, user, setValue, applyPayloadOrPreset]);

  const handleResetPreset = useCallback(async () => {
    if (isLocked) {
      showAlert('확정된 구조는 프리셋을 적용할 수 없습니다.', 'warning');
      return;
    }
    const ok = await showConfirm('현재 수지구분 기본 프리셋으로 되돌릴까요?', 'warning');
    if (!ok) return;
    grid.setData(clonePreset(getValues('inoutDiv'), STRUCTURE_PURPOSE));
    setStatusLabel('작성중');
    setValue('status', '작성중');
    refreshValidation();
  }, [grid, getValues, setValue, refreshValidation, isLocked]);

  const handleAddRow = useCallback(() => {
    if (isLocked) {
      showAlert('확정된 구조는 행을 추가할 수 없습니다.', 'warning');
      return;
    }
    const n = Math.min((gridRef.current?.getData?.()?.length || 0) + 1, 8);
    grid.handleAdd({
      levelNo: n,
      levelCode: `LV${n}`,
      codeRule: '자체',
      finalYn: 'N',
      acctMapYn: 'N',
    });
  }, [grid, isLocked]);

  const handleDeleteRow = useCallback(() => {
    if (isLocked) {
      showAlert('확정된 구조는 행을 삭제할 수 없습니다.', 'warning');
      return;
    }
    grid.handleDelete();
  }, [grid, isLocked]);

  const getExportData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getExportColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '예산과목구조',
    getData: getExportData,
    getDataColumns: getExportColumns,
    joinKeys: ['levelCode'],
    order: 0,
  });

  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '예산과목구조' });

  const searchFields = [
    [
      {
        name: 'fiscalYear',
        label: '회계연도',
        type: 'fiscalYear',
        width: CommonCss.w90,
        labelWidth: SEARCH_LABEL_W,
        min: 2000,
        max: 2099,
      },
      {
        name: 'inoutDiv',
        label: '수지구분',
        type: 'select',
        width: CommonCss.w80,
        labelWidth: SEARCH_LABEL_W,
        options: INOUT_OPTIONS,
      },
      { name: 'status', label: '상태', type: 'text', width: CommonCss.w70, labelWidth: SEARCH_LABEL_W, readOnly: true },
    ],
  ];

  const actionButtons = [
    { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
    { id: 'add', label: '추가', icon: '➕', onClick: handleAddRow, hotkey: 'F8' },
    { id: 'delete', label: '삭제', icon: '❌', onClick: handleDeleteRow, hotkey: 'F9', variant: 'danger' },
    { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
    excelButton,
  ];

  const extraButtons = [
    { id: 'confirm', label: '구조확정', onClick: handleConfirm },
    { id: 'unconfirm', label: '확정해제', onClick: handleUnconfirm },
    { id: 'migrateYear', label: '차년도이관', onClick: handleOpenMigrate },
    { id: 'preset', label: '프리셋', onClick: handleResetPreset },
  ];

  const columnPreview = validation.used.length
    ? buildBudgetLevelColumnFields(validation.used)
        .map((c) => c.header)
        .join(' · ')
    : '-';

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup
          buttons={actionButtons}
          extraButtons={extraButtons}
          loading={grid.loading}
        />
        <CollapsibleSearchPanel
          storageKey="budget-subject-structure"
          prefsUserId={prefsUserId}
          summary="회계연도·구조 조회"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Layout>
        <Panel>
          <PanelHead>
            레벨구조 그리드 — 최종레벨 · 분개매핑
            {isLocked ? ' · [확정잠금]' : ''}
          </PanelHead>
          <div style={{ flex: 1, minHeight: 280, padding: 8 }}>
            <GridExportFocusScope gridId={GRID_ID}>
              <HiGridComponent
                ref={gridRef}
                columns={columns}
                height="100%"
                showRowNumber
                rowNumberSize={56}
                showModColumn
                {...grid.contextMenuProps}
                enableHeaderMenu
                enableSelectionStats
                enableFreeze
                enableColumnFilters
                enableQuickExport
                exportFileName="예산과목구조"
                gridId={GRID_ID}
                prefsUserId={prefsUserId}
                colOptions={grid.colOptions}
              />
            </GridExportFocusScope>
          </div>
        </Panel>

        <Panel>
          <PanelHead>검증 / 미리보기</PanelHead>
          <PreviewPath>{validation.previewText}</PreviewPath>
          <MetaLine>
            버전 {meta.versionNo || 1}
            {meta.confirmedBy
              ? ` · 확정자 ${meta.confirmedBy} · ${String(meta.confirmedAt).replace('T', ' ').slice(0, 19)}`
              : ' · 미확정'}
            {isLocked ? ' · 확정잠금' : ''}
          </MetaLine>
          <CheckList>
            <CheckItem>
              <span>레벨(LV) 연속</span>
              <Badge $ok={validation.continuous}>{validation.continuous ? '정상' : '오류'}</Badge>
            </CheckItem>
            <CheckItem>
              <span>최종레벨 1건</span>
              <Badge $ok={validation.finalOne}>{validation.finalOne ? '정상' : '오류'}</Badge>
            </CheckItem>
            <CheckItem>
              <span>구조확정 가능</span>
              <Badge $ok={validation.canConfirm}>{validation.canConfirm ? '가능' : '불가'}</Badge>
            </CheckItem>
            <CheckItem>
              <span>상태</span>
              <Badge $ok={statusLabel === '확정'}>{statusLabel}</Badge>
            </CheckItem>
          </CheckList>
          <CardTitle style={{ margin: '0 14px 6px', fontSize: '0.85rem' }}>하위 화면 컬럼 미리보기</CardTitle>
          <Hint>
            편성 · 배정 · 품의 · 원인행위 · 지출결의에 적용될 과목 컬럼:
            <br />
            <strong>{columnPreview}</strong>
            {validation.acctMapLevelNames?.length ? (
              <>
                <br />
                분개매핑 가능 레벨: <strong>{validation.acctMapLevelNames.join(', ')}</strong>
              </>
            ) : null}
          </Hint>
          <CardTitle style={{ margin: '0 14px 6px', fontSize: '0.85rem' }}>확정 시 영향 범위</CardTitle>
          <ImpactList>
            {DOWNSTREAM_IMPACT_SCREENS.map((s) => (
              <li key={s.id}>{s.label}</li>
            ))}
          </ImpactList>
        </Panel>
      </Layout>
      {excelDialog}
      <YearMigrateDialog
        open={migrateOpen}
        kind="subject"
        dims={dimsFromForm()}
        structureStatus={statusLabel}
        onClose={() => setMigrateOpen(false)}
        onConfirm={handleMigrateConfirm}
      />
    </PageContainer>
  );
};

const BudgetSubjectStructure = () => (
  <GridExportProvider>
    <BudgetSubjectStructureInner />
  </GridExportProvider>
);

export default BudgetSubjectStructure;
